(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports, require('parsimmon')) :
  typeof define === 'function' && define.amd ? define(['exports', 'parsimmon'], factory) :
  (global = global || self, factory(global.epubjs = {}, global.P));
}(this, (function (exports, P) { 'use strict';

  const generateUUID = () => {
      let d = new Date().getTime();
      const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
          const r = (d + Math.random() * 16) % 16 | 0;
          d = Math.floor(d / 16);
          return (c === "x" ? r : (r & 0x7) | 0x8).toString(16);
      });
      return uuid;
  };
  const encodeCFIString = (cfi) => {
      const doNotEncode = ["!", ":", "/"];
      let encodedCFI = "";
      for (let i = 0; i < cfi.length; i++) {
          if (doNotEncode.indexOf(cfi[i]) === -1) {
              encodedCFI += encodeURIComponent(cfi[i]);
          }
          else {
              encodedCFI += cfi[i];
          }
      }
      return encodedCFI;
  };
  const specialCharacters = ["^", "[", "]", "(", ")", ",", ";", "="];
  const escapeCFIString = (cfi) => {
      let escapedCfi = "";
      for (let i = 0; i < cfi.length; i++) {
          if (specialCharacters.indexOf(cfi[i]) !== -1) {
              escapedCfi += `^${cfi[i]}`;
          }
          else {
              escapedCfi += cfi[i];
          }
      }
      return escapedCfi;
  };
  const unescapeCFIString = (cfi) => {
      if (cfi === null) {
          return cfi;
      }
      let unescapedCfi = "";
      for (let i = 0; i < cfi.length; i++) {
          if (cfi[i] === "^" && specialCharacters.indexOf(cfi[i + 1]) !== -1) {
              unescapedCfi += cfi[i + 1];
              i++;
          }
          else {
              unescapedCfi += cfi[i];
          }
      }
      return unescapedCfi;
  };
  const escapeRegExp = (str) => {
      return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  const separator = P.string("/");
  const indirection = P.string("!");
  const int = P.digits.map(Number);
  const float = P.seqMap(P.digits, P.string("."), P.digits, (n, _s, d) => {
      return Number(`${n}.${d}`);
  });
  const number = float.or(int);
  const assertion = P.string("[").chain(function (_start) {
      let allowNext = false;
      return P.takeWhile(function (c) {
          if (allowNext) {
              allowNext = false;
              return true;
          }
          if (c === "]") {
              return false;
          }
          else {
              allowNext = c === "^";
          }
          return true;
      }).skip(P.string("]"));
  });
  const parseCharOffsetAssertion = (a) => {
      if (a === null) {
          return a;
      }
      if (a[0] === ",") {
          return {
              precedingText: "",
              trailingText: unescapeCFIString(a.slice(1)) || "",
          };
      }
      const separatorIndex = a.search(/[^^],/);
      if (separatorIndex === -1) {
          return {
              precedingText: unescapeCFIString(a) || "",
              trailingText: "",
          };
      }
      else {
          return {
              precedingText: unescapeCFIString(a.slice(0, separatorIndex + 1)) || "",
              trailingText: unescapeCFIString(a.slice(separatorIndex + 2)) || "",
          };
      }
  };
  const charOffset = P.seqMap(P.string(":"), int, assertion.fallback(null), (_, n, a) => {
      return { offset: n, assert: parseCharOffsetAssertion(a) };
  });
  const spatialOffset = P.seqMap(P.string("@"), number, P.string(":"), number, (_a, x, _s, y) => {
      return { x, y };
  });
  const temporalOffset = P.string("~").then(number);
  const step = P.seqMap(separator, number, assertion.fallback(null), (_s, n, a) => {
      return { step: n, assert: unescapeCFIString(a) };
  });
  const docAndIndirection = P.seqMap(step.many(), indirection, (steps, _) => {
      return new CFI({ steps });
  });
  const cfiParser = P.seqMap(docAndIndirection.fallback(null), step.many(), charOffset.fallback(null), temporalOffset.fallback(null), spatialOffset.fallback(null), (contentDoc, steps, charOffset, temporalOffset, spatialOffset) => {
      return new CFI({
          contentDoc,
          steps,
          charOffset,
          spatialOffset,
          temporalOffset,
      });
  });
  const cfiRangeParser = P.seqMap(cfiParser, P.string(","), cfiParser, P.string(","), cfiParser, (base, _comma1, start, _comma2, end) => {
      const baseString = base.toString();
      const startString = start.toString();
      const endString = end.toString();
      if (!baseString || start === undefined || end === undefined) {
          throw new Error(`cfi is not a valid range (wrong number of pieces)`);
      }
      if (CFI.compare(start, end) === 1) {
          throw new Error(`cfi is not a valid range (start is after end)`);
      }
      return new CFIRange({
          base,
          start: parseCfi(baseString + startString),
          end: parseCfi(baseString + endString),
      });
  });

  const decodeIfNecessary = function (cfi, isRange = false) {
      if (isRange && cfi.match(/\^|\[|\]|;|=/)) {
          return cfi;
      }
      else if (cfi.match(/\^|\[|\]|;|=/)) {
          return cfi;
      }
      try {
          return decodeURIComponent(cfi);
      }
      catch (e) {
          return cfi;
      }
  };
  const parseOrThrow = function (cfi, parser) {
      const result = parser.parse(cfi);
      if (!result.status) {
          throw new Error(`${cfi} does not look valid`);
      }
      const r = result;
      return r.value;
  };
  const parseCfi = function (cfi) {
      return parseOrThrow(decodeIfNecessary(cfi), cfiParser);
  };
  const parseCfiRange = function (cfi) {
      return parseOrThrow(decodeIfNecessary(cfi), cfiRangeParser);
  };

  var Comparator;
  (function (Comparator) {
      Comparator[Comparator["LESS_THAN"] = -1] = "LESS_THAN";
      Comparator[Comparator["EQUAL"] = 0] = "EQUAL";
      Comparator[Comparator["GREATER_THAN"] = 1] = "GREATER_THAN";
  })(Comparator || (Comparator = {}));
  class Step {
      constructor({ step, assert }) {
          this.step = step;
          this.assert = assert;
      }
      static equals(a, b, opts = { checkAssertions: false }) {
          if (opts.checkAssertions && a.assert && b.assert) {
              return a.assert === b.assert;
          }
          return a.step === b.step;
      }
      static compare(a, b) {
          if (a.step < b.step) {
              return Comparator.LESS_THAN;
          }
          if (a.step > b.step) {
              return Comparator.GREATER_THAN;
          }
          return Comparator.EQUAL;
      }
  }
  const getCollectedStep = (fullString, startIndex) => {
      let collected = fullString[startIndex];
      let tempIndex = startIndex;
      while (!isNaN(parseInt(fullString[tempIndex + 1]))) {
          collected = collected + fullString[tempIndex + 1];
          tempIndex++;
      }
      return collected;
  };
  class CFIRange {
      constructor({ base, start, end }) {
          this.toString = (options = {}) => {
              const baseString = this.base.toString(options);
              return `${baseString},${this.start
                .toString(options)
                .slice(baseString.length)},${this.end
                .toString(options)
                .slice(baseString.length)}`;
          };
          this.getBase = () => this.base;
          this.getStart = () => this.start;
          this.getEnd = () => this.end;
          if (base) {
              this.base = base;
              this.start = start;
              this.end = end;
          }
          else {
              const s = start.toString();
              const e = end.toString();
              let begin = "";
              let i;
              for (i = 0; i < Math.min(s.length, e.length); i) {
                  const collectedS = getCollectedStep(s, i);
                  const collectedE = getCollectedStep(e, i);
                  if (collectedS === collectedE && [":", "@", "~"].indexOf(s[i]) !== -1) {
                      begin += s[i];
                      break;
                  }
                  else if (collectedS === collectedE) {
                      begin += collectedS;
                      i = i + collectedS.length;
                  }
                  else {
                      break;
                  }
              }
              if (["/", ":", "@", "~", "!"].indexOf(begin[begin.length - 1]) !== -1) {
                  begin = begin.slice(0, -1);
              }
              const range = parseCfiRange(`${begin},${s.slice(begin.length)},${e.slice(begin.length)}`);
              this.base = range.base;
              this.start = range.start;
              this.end = range.end;
          }
      }
  }
  class CFI {
      constructor({ steps = [], contentDoc, charOffset, spatialOffset, temporalOffset, }) {
          this.toString = (options = {}) => {
              const defaultOpts = { assertions: true, uriEncoded: false };
              const opts = Object.assign(Object.assign({}, defaultOpts), options);
              let s = "";
              if (this.contentDoc) {
                  s = this.contentDoc.toString(Object.assign(Object.assign({}, options), { uriEncoded: false })) + "!";
              }
              s = this.steps.reduce((acc, { step, assert }) => {
                  acc += `/${step}`;
                  if (opts.assertions && assert) {
                      acc += `[${assert}]`;
                  }
                  return acc;
              }, s);
              if (this.charOffset) {
                  s += `:${this.charOffset.offset}`;
                  if (opts.assertions && this.charOffset.assert) {
                      s += `[${escapeCFIString(this.charOffset.assert.precedingText || "")}${this.charOffset.assert.trailingText
                        ? `,${escapeCFIString(this.charOffset.assert.trailingText)}`
                        : ""}]`;
                  }
              }
              if (this.temporalOffset) {
                  s += `~${this.temporalOffset}`;
              }
              if (this.spatialOffset) {
                  const sp = this.spatialOffset;
                  s += `@${sp.x}:${sp.y}`;
              }
              if (opts.uriEncoded) {
                  s = encodeCFIString(s);
              }
              return s;
          };
          this.steps = steps.map((s) => new Step(s));
          if (contentDoc)
              this.contentDoc = new CFI(contentDoc);
          this.charOffset = charOffset;
          this.spatialOffset = spatialOffset;
          this.temporalOffset = temporalOffset || null;
      }
      static equals(a, b, opts = {
          compareOffsets: false,
          checkAssertions: false,
      }) {
          var _a, _b, _c, _d;
          if (a.contentDoc && b.contentDoc) {
              const docsEqual = CFI.equals(a.contentDoc, b.contentDoc, opts);
              if (!docsEqual) {
                  return false;
              }
          }
          else if ((a.contentDoc && !b.contentDoc) ||
              (!a.contentDoc && b.contentDoc)) {
              return false;
          }
          for (let i = 0; i < a.steps.length; i++) {
              if (!b.steps[i]) {
                  return false;
              }
              const equalSteps = Step.equals(a.steps[i], b.steps[i], opts);
              if (!equalSteps) {
                  return false;
              }
          }
          if (a.steps.length < b.steps.length) {
              return false;
          }
          if (opts.compareOffsets) {
              if (a.charOffset && b.charOffset) {
                  if (opts.checkAssertions) {
                      return (((_a = a.charOffset.assert) === null || _a === void 0 ? void 0 : _a.precedingText) === ((_b = b.charOffset.assert) === null || _b === void 0 ? void 0 : _b.precedingText) &&
                          ((_c = a.charOffset.assert) === null || _c === void 0 ? void 0 : _c.trailingText) === ((_d = b.charOffset.assert) === null || _d === void 0 ? void 0 : _d.trailingText));
                  }
                  else {
                      return a.charOffset.offset === b.charOffset.offset;
                  }
              }
              else if (a.charOffset || b.charOffset) {
                  return false;
              }
              if (a.spatialOffset && b.spatialOffset) {
                  return (a.spatialOffset.x === b.spatialOffset.x &&
                      a.spatialOffset.y === b.spatialOffset.y);
              }
              else if (a.spatialOffset || b.spatialOffset) {
                  return false;
              }
              if (a.temporalOffset !== null && b.temporalOffset !== null) {
                  return a.temporalOffset === b.temporalOffset;
              }
              else if (a.temporalOffset !== null || b.temporalOffset !== null) {
                  return false;
              }
          }
          return true;
      }
      static compare(a, b, opts = { compareOffsets: false }) {
          if (a.contentDoc && b.contentDoc) {
              const docResult = CFI.compare(a.contentDoc, b.contentDoc);
              if (docResult !== Comparator.EQUAL) {
                  return docResult;
              }
          }
          for (let i = 0; i < a.steps.length; i++) {
              if (!b.steps[i]) {
                  return Comparator.GREATER_THAN;
              }
              const result = Step.compare(a.steps[i], b.steps[i]);
              if (result !== Comparator.EQUAL) {
                  return result;
              }
          }
          if (a.steps.length < b.steps.length) {
              return Comparator.LESS_THAN;
          }
          if (opts.compareOffsets) {
              if (a.charOffset && b.charOffset) {
                  if (a.charOffset.offset === b.charOffset.offset) {
                      return Comparator.EQUAL;
                  }
                  else if (a.charOffset.offset < b.charOffset.offset) {
                      return Comparator.LESS_THAN;
                  }
                  else if (a.charOffset.offset > b.charOffset.offset) {
                      return Comparator.GREATER_THAN;
                  }
              }
              else {
                  if (a.charOffset && !b.charOffset) {
                      return Comparator.GREATER_THAN;
                  }
                  if (!a.charOffset && b.charOffset) {
                      return Comparator.LESS_THAN;
                  }
              }
              if (a.temporalOffset !== null && b.temporalOffset !== null) {
                  if (a.temporalOffset === b.temporalOffset) {
                      return Comparator.EQUAL;
                  }
                  else if (a.temporalOffset < b.temporalOffset) {
                      return Comparator.LESS_THAN;
                  }
                  else if (a.temporalOffset > b.temporalOffset) {
                      return Comparator.GREATER_THAN;
                  }
              }
              else if (a.temporalOffset !== null && b.temporalOffset === null) {
                  return Comparator.GREATER_THAN;
              }
              else if (a.temporalOffset === null && b.temporalOffset !== null) {
                  return Comparator.LESS_THAN;
              }
              if (a.spatialOffset && b.spatialOffset) {
                  if (a.spatialOffset.x === b.spatialOffset.x &&
                      a.spatialOffset.y === b.spatialOffset.y) {
                      return Comparator.EQUAL;
                  }
                  if (a.spatialOffset.y > b.spatialOffset.y) {
                      return Comparator.GREATER_THAN;
                  }
                  else if (a.spatialOffset.y < b.spatialOffset.y) {
                      return Comparator.LESS_THAN;
                  }
                  else {
                      if (a.spatialOffset.x < b.spatialOffset.x) {
                          return Comparator.LESS_THAN;
                      }
                      else if (a.spatialOffset.x > b.spatialOffset.x) {
                          return Comparator.GREATER_THAN;
                      }
                  }
              }
              else {
                  if (a.spatialOffset && !b.spatialOffset) {
                      return Comparator.GREATER_THAN;
                  }
                  if (!a.spatialOffset && b.spatialOffset) {
                      return Comparator.LESS_THAN;
                  }
              }
          }
          return Comparator.EQUAL;
      }
  }

  const VST_IGNORE = "vstignore";
  const VST_SKIP = "vstskip";
  const IDENTIFIER_ATTRIBUTE = "data-markup-id";
  const VST_DONT_HIGHLIGHT = "vstdonthighlight";
  const VST_SKIP_ASSERTION = "vstskipassertion";
  const shouldIgnoreNode = (node, options) => options.ignoreFn
      ? options.ignoreFn(node, options)
      : hasClass(node, VST_IGNORE);
  const shouldSkipAssertion = (node) => hasClass(node, VST_SKIP_ASSERTION);
  const isMathNode = (node, options) => {
      var _a;
      if (isTextNode(node)) {
          return false;
      }
      if (node.namespaceURI === "http://www.w3.org/1998/Math/MathML" &&
          node.localName === "math") {
          return true;
      }
      else if (node.namespaceURI === "http://www.w3.org/2000/svg" &&
          node.localName === "svg") {
          return true;
      }
      else if (node.getAttribute("id") === "MathJax_Message") {
          return true;
      }
      else if (hasClass(node, "MathJax")) {
          return true;
      }
      else if (node.getAttribute && node.getAttribute("role") === "math") {
          return true;
      }
      else if (((_a = node.children[0]) === null || _a === void 0 ? void 0 : _a.getAttribute("id")) === "MathJax_SVG_Hidden") {
          return true;
      }
      return false;
  };
  const shouldSkipNode = (node, options) => options.skipFn
      ? options.skipFn(node, options)
      : node.nodeType === Node.COMMENT_NODE ||
          hasClass(node, VST_SKIP) ||
          isMathNode(node);
  const hasClass = (node, className) => node.classList && node.classList.contains(className);
  const shouldMarkupTextNode = (node) => {
      var _a, _b, _c;
      let needsSpan = true;
      const mathMarkupNames = ["mtext", "mi", "mn", "mo"];
      const tableElementNames = [
          "table",
          "tr",
          "thead",
          "tbody",
          "tfoot",
          "colgroup",
          "col",
      ];
      const nodeParentName = ((_a = node.parentElement) === null || _a === void 0 ? void 0 : _a.localName) || ((_b = node.parentElement) === null || _b === void 0 ? void 0 : _b.nodeName);
      if (((_c = node.parentElement) === null || _c === void 0 ? void 0 : _c.namespaceURI) === "http://www.w3.org/1998/Math/MathML") {
          if (nodeParentName &&
              mathMarkupNames.indexOf(nodeParentName.toLowerCase()) === -1) {
              needsSpan = false;
          }
      }
      if (nodeParentName &&
          tableElementNames.indexOf(nodeParentName.toLowerCase()) !== -1) {
          needsSpan = false;
      }
      return needsSpan;
  };
  const getCFIForNode = (node, options) => parseCfi(getCFIStringForNode(node, options || {}));
  const getCFIStringForNode = (node, options) => {
      const opts = options || {};
      const parent = getParent(node, opts);
      if (parent) {
          const step = getStepForNode(node, opts);
          if (step) {
              let relative = `/${step.step}`;
              if (step.assert) {
                  relative = `${relative}[${escapeCFIString(step.assert)}]`;
              }
              return getCFIStringForNode(parent, opts) + relative;
          }
          return "";
      }
      return "";
  };
  const isValidStep = (node, options) => node.nodeType === 1 &&
      !(shouldSkipNode(node, options) || shouldIgnoreNode(node, options));
  const getParent = (node, options) => {
      if (!node) {
          return null;
      }
      let parent = node.parentElement;
      while (parent && !isValidStep(parent, options)) {
          parent = parent.parentElement;
      }
      return parent;
  };
  const getChildren = (node, options) => {
      return Array.from(node.childNodes).reduce((acc, child) => {
          if (child.nodeType === 3) {
              if (child.textContent && child.textContent.replace(/\s/g, "").length) {
                  return acc.concat([child]);
              }
              return acc;
          }
          else if (child.nodeType === 1) {
              if (shouldSkipNode(child, options)) {
                  return acc;
              }
              else if (shouldIgnoreNode(child, options)) {
                  return acc.concat(getChildren(child, options));
              }
              else {
                  return acc.concat([child]);
              }
          }
          else {
              return acc;
          }
      }, []);
  };
  const getStepForNode = (node, options) => {
      const parent = getParent(node, options);
      if (parent) {
          const siblings = getChildren(parent, options);
          let currentStep = 0;
          for (let i = 0; i < siblings.length; i++) {
              const sib = siblings[i];
              if (sib.nodeType === 1) {
                  currentStep++;
                  if (currentStep % 2 !== 0) {
                      currentStep++;
                  }
              }
              else if (sib.nodeType === 3) {
                  if (currentStep % 2 === 0) {
                      currentStep++;
                  }
              }
              if (sib === node) {
                  const nodeElement = node;
                  return new Step({
                      step: currentStep,
                      assert: !shouldSkipAssertion(nodeElement)
                          ? nodeElement.id
                          : undefined,
                  });
              }
          }
          return null;
      }
      else {
          return null;
      }
  };
  const getChildAtStep = (node, step, options) => {
      if (!node) {
          return null;
      }
      let currentStep = 0;
      const doc = node.ownerDocument;
      const children = getChildren(node, options);
      const child = children.find((child) => {
          if (child.nodeType === 1) {
              currentStep++;
              if (currentStep % 2 !== 0) {
                  currentStep++;
              }
          }
          else if (isTextNode(child)) {
              if (currentStep % 2 === 0) {
                  currentStep++;
              }
          }
          return currentStep === step.step;
      }) || null;
      if (step.assert && (!child || child.id !== step.assert)) {
          return (doc === null || doc === void 0 ? void 0 : doc.getElementById(step.assert)) || null;
      }
      return child;
  };
  const findPositionalNode = (node, options) => {
      if (!node) {
          return null;
      }
      const el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
      if (!el) {
          return null;
      }
      const rect = el.getClientRects();
      if (rect.length && !shouldSkipNode(el, options)) {
          return el;
      }
      let target = el;
      target = el;
      while (target) {
          target = target.previousElementSibling;
          if (target &&
              !shouldSkipNode(target, options) &&
              target.getClientRects().length > 0) {
              return target;
          }
      }
      target = el;
      while (target) {
          target = target.nextElementSibling;
          if (target &&
              !shouldSkipNode(target, options) &&
              target.getClientRects().length > 0) {
              return target;
          }
      }
      return findPositionalNode(el.parentElement, options);
  };
  const getNodeForCFI = (doc, cfi, options) => {
      const opts = options || {};
      let node = doc.firstElementChild;
      for (let i = 0; i < cfi.steps.length; i++) {
          const child = getChildAtStep(node, cfi.steps[i], opts);
          if (child) {
              node = child;
          }
          else {
              if (opts.allowNull) {
                  node = null;
              }
              break;
          }
      }
      if (node && isTextNode(node) && cfi.charOffset) {
          const possibleNodes = [node];
          let previous = getPreviousSibling(node, opts);
          while (previous && isTextNode(previous)) {
              possibleNodes.unshift(previous);
              previous = getPreviousSibling(previous, opts);
          }
          let next = getNextSibling(node, opts);
          while (next && isTextNode(next)) {
              possibleNodes.push(next);
              next = getNextSibling(next, opts);
          }
          if (possibleNodes.length > 0) {
              const summary = possibleNodes.reduce((acc, n) => {
                  const nodeContent = n.textContent || "";
                  return {
                      fullString: acc.fullString + nodeContent,
                      nodes: [
                          ...acc.nodes,
                          {
                              node: n,
                              startIndex: acc.fullString.length,
                              endIndex: acc.fullString.length + nodeContent.length,
                          },
                      ],
                  };
              }, { fullString: "", nodes: [] });
              const index = findClosestIndexToOffset(cfi.charOffset, summary.fullString, 0);
              const foundNode = summary.nodes.find((n) => n.startIndex <= index && n.endIndex > index) || summary.nodes[summary.nodes.length - 1];
              node = foundNode ? foundNode.node : null;
          }
      }
      if (node && opts.isPositional) {
          node = findPositionalNode(node, opts);
      }
      return node;
  };
  const getTextOffset = (node, offset, options = {}) => {
      const normalizedTextNodes = getNormalizedTextNodes(node, options);
      const selectedIndex = normalizedTextNodes.findIndex((ntn) => ntn === node);
      const preNodeContent = normalizedTextNodes
          .slice(0, selectedIndex)
          .map((ntn) => ntn.textContent)
          .join("");
      const correctedOffset = preNodeContent.length + offset;
      return correctedOffset;
  };
  const getTextOffsetAssertion = (node, offset, options) => {
      const normalizedTextNodes = getNormalizedTextNodes(node, options);
      const nodeContentText = normalizedTextNodes
          .map((ntn) => ntn.textContent)
          .join("");
      if (nodeContentText) {
          const before = nodeContentText.slice(Math.max(0, offset - 3), offset);
          const after = nodeContentText.slice(offset, Math.min(nodeContentText.length, offset + 3));
          if (after.length) {
              return {
                  precedingText: before,
                  trailingText: after,
              };
          }
          else {
              return { precedingText: before, trailingText: "" };
          }
      }
      else {
          return null;
      }
  };
  const getCFIForRange = (range, options) => {
      const startCFI = getCFIForNode(range.startContainer, options);
      const startOffset = getTextOffset(range.startContainer, range.startOffset, options);
      startCFI.charOffset = {
          offset: startOffset,
          assert: getTextOffsetAssertion(range.startContainer, startOffset, options || {}),
      };
      const endCFI = getCFIForNode(range.endContainer, options);
      const endOffset = getTextOffset(range.endContainer, range.endOffset, options);
      endCFI.charOffset = {
          offset: endOffset,
          assert: getTextOffsetAssertion(range.endContainer, endOffset, options || {}),
      };
      return new CFIRange({ start: startCFI, end: endCFI });
  };
  const isTextNode = (node, _options) => {
      const type = node.nodeType;
      return type === 3 || type === 4 || type === 5;
  };
  const getNextParentSibling = (node, parentStop, options) => {
      var _a, _b;
      if (!node) {
          return null;
      }
      if ((_a = node.parentElement) === null || _a === void 0 ? void 0 : _a.nextSibling) {
          return (_b = node.parentElement) === null || _b === void 0 ? void 0 : _b.nextSibling;
      }
      if (node !== parentStop) {
          return getNextParentSibling(node.parentElement, parentStop);
      }
      return null;
  };
  const getNextSibling = (node, options) => {
      if (node) {
          let nextSibling = node.nextSibling;
          if (!nextSibling) {
              const parent = node.parentElement;
              if (parent && shouldIgnoreNode(parent, options)) {
                  return getNextSibling(parent, options);
              }
              else {
                  return null;
              }
          }
          if (isTextNode(nextSibling)) {
              return nextSibling;
          }
          if (shouldSkipNode(nextSibling, options)) {
              return getNextSibling(nextSibling, options);
          }
          while (nextSibling && shouldIgnoreNode(nextSibling, options)) {
              nextSibling = (nextSibling === null || nextSibling === void 0 ? void 0 : nextSibling.firstChild) || null;
              if (nextSibling && isTextNode(nextSibling)) {
                  return nextSibling;
              }
          }
          return nextSibling;
      }
      return null;
  };
  const getPreviousSibling = (node, options) => {
      if (node) {
          let previousSibling = node.previousSibling;
          if (!previousSibling) {
              const parent = node.parentElement;
              if (parent && shouldIgnoreNode(parent, options)) {
                  return getPreviousSibling(parent, options);
              }
              else {
                  return null;
              }
          }
          if (isTextNode(previousSibling)) {
              return previousSibling;
          }
          if (shouldSkipNode(previousSibling, options)) {
              return getPreviousSibling(previousSibling, options);
          }
          while (previousSibling &&
              shouldIgnoreNode(previousSibling, options)) {
              previousSibling = (previousSibling === null || previousSibling === void 0 ? void 0 : previousSibling.lastChild) || null;
              if (previousSibling && isTextNode(previousSibling)) {
                  return previousSibling;
              }
          }
          return previousSibling;
      }
      return null;
  };
  const getNormalizedTextNodes = (node, options = {}) => {
      if (isTextNode(node)) {
          const nodeSet = [node];
          let previous = getPreviousSibling(node, options);
          while (previous && isTextNode(previous)) {
              nodeSet.unshift(previous);
              previous = getPreviousSibling(previous, options);
          }
          let next = getNextSibling(node, options);
          while (next && isTextNode(next)) {
              nodeSet.push(next);
              next = getNextSibling(next, options);
          }
          return nodeSet;
      }
      else {
          return [];
      }
  };
  const getTextNodeSetsInRange = (doc, range, options) => {
      const parentNode = getNodeForCFI(doc, range.base, options);
      const startNode = getNodeForCFI(doc, range.start, options);
      const endNode = getNodeForCFI(doc, range.end, options);
      if (!parentNode || !startNode || !endNode) {
          return [];
      }
      if (startNode !== endNode &&
          endNode.compareDocumentPosition(startNode) &
              Node.DOCUMENT_POSITION_FOLLOWING) {
          return [];
      }
      const textNodes = [];
      let current = startNode;
      while (current) {
          if (isTextNode(current)) {
              const nodeSet = [current];
              let previous = getPreviousSibling(current, options);
              while (previous && isTextNode(previous)) {
                  nodeSet.unshift(previous);
                  previous = getPreviousSibling(previous, options);
              }
              let next = getNextSibling(current, options);
              while (next && isTextNode(next)) {
                  if (current === endNode) {
                      textNodes.push(nodeSet);
                      return textNodes;
                  }
                  nodeSet.push(next);
                  current = next;
                  next = getNextSibling(next, options);
              }
              textNodes.push(nodeSet);
              if (current === endNode) {
                  return textNodes;
              }
              current =
                  (current === null || current === void 0 ? void 0 : current.nextSibling) ||
                      getNextParentSibling(current, parentNode);
          }
          else {
              if (current === endNode) {
                  return textNodes;
              }
              if (shouldSkipNode(current, options)) {
                  current =
                      current.nextSibling ||
                          getNextParentSibling(current, parentNode);
              }
              else {
                  current =
                      current.firstChild ||
                          current.nextSibling ||
                          getNextParentSibling(current, parentNode);
              }
          }
      }
      return textNodes;
  };
  const findClosestIndexToOffset = (charOffset, existingText, defaultIndex = 0, _options) => {
      if (!charOffset) {
          return defaultIndex;
      }
      const index = charOffset.offset || defaultIndex;
      const assert = charOffset.assert;
      if (assert) {
          const { precedingText, trailingText } = assert;
          const combined = precedingText + trailingText;
          if (existingText.slice(index - ((precedingText === null || precedingText === void 0 ? void 0 : precedingText.length) || 0), index + ((trailingText === null || trailingText === void 0 ? void 0 : trailingText.length) || 0)) !== combined) {
              const re = new RegExp(escapeRegExp(combined), "ig");
              let r;
              const results = [];
              while ((r = re.exec(existingText))) {
                  results.push(r.index + precedingText.length);
              }
              const result = results.reduce((closest, result) => {
                  const distance = Math.abs(result - index);
                  const closestDistance = closest.distance;
                  if (!closestDistance || distance < closestDistance) {
                      return { distance, result };
                  }
                  return closest;
              }, { distance: null, result: null }).result;
              return result === null ? index : result;
          }
      }
      return index;
  };
  const getCorrectedCFI = (doc, cfi, options) => {
      const node = getNodeForCFI(doc, cfi, options);
      if (node) {
          return getCFIForNode(node, options);
      }
      return null;
  };
  const getTextNodes = (nodes, options) => {
      return nodes.reduce((acc, node) => {
          if (isTextNode(node)) {
              return [...acc, node];
          }
          else {
              return [...acc, ...getTextNodes(Array.from(node.childNodes))];
          }
      }, []);
  };
  const markupNodes = (nodes, arg3, arg4, options) => {
      if (nodes.length) {
          const doc = nodes[0].ownerDocument;
          if (doc) {
              let spanIdentifier = generateUUID();
              let renderFn;
              if (typeof arg3 === "string") {
                  spanIdentifier = arg3;
              }
              else if (typeof arg3 === "function") {
                  renderFn = arg3;
              }
              if (typeof arg4 === "string") {
                  spanIdentifier = arg4;
              }
              else if (typeof arg4 === "function") {
                  renderFn = arg4;
              }
              const textNodes = getTextNodes(nodes);
              const markedUpNodes = textNodes.map((tn, index) => {
                  var _a;
                  const newChild = doc.createElement("span");
                  newChild.textContent = tn.textContent;
                  newChild.classList.add(VST_IGNORE);
                  newChild.setAttribute(IDENTIFIER_ATTRIBUTE, spanIdentifier);
                  if (renderFn)
                      renderFn(newChild, index);
                  (_a = tn.parentNode) === null || _a === void 0 ? void 0 : _a.insertBefore(newChild, tn);
                  tn.remove();
                  return newChild;
              });
              return {
                  nodes: markedUpNodes,
                  identifier: spanIdentifier,
                  unmark: () => unmarkDOM(doc, { identifier: spanIdentifier }),
              };
          }
      }
      return {
          nodes: [],
          identifier: "",
          unmark: () => {
              return;
          },
      };
  };
  const markupHighlight = (doc, range, arg3, arg4, options) => {
      return markupDOM(doc, range, arg3, arg4, Object.assign(Object.assign({}, options), { skipFn: (node, options) => {
              return (((options === null || options === void 0 ? void 0 : options.skipFn) && options.skipFn(node, {})) ||
                  shouldSkipNode(node, {}) ||
                  hasClass(node, VST_DONT_HIGHLIGHT));
          } }));
  };
  const recursivelyRemove = (node) => {
      node.replacedByTheseTextNodes &&
          node.replacedByTheseTextNodes.forEach((nn) => {
              if (nn.ignoreNode) {
                  nn.ignoreNode.remove();
              }
              else {
                  nn.remove();
              }
              recursivelyRemove(nn);
          });
      node.remove();
  };
  const markupDOM = (doc, range, arg3, arg4, options) => {
      const epubOptions = options || {};
      let spanIdentifier = generateUUID();
      let renderFn;
      if (typeof arg3 === "string") {
          spanIdentifier = arg3;
      }
      else if (typeof arg3 === "function") {
          renderFn = arg3;
      }
      if (typeof arg4 === "string") {
          spanIdentifier = arg4;
      }
      else if (typeof arg4 === "function") {
          renderFn = arg4;
      }
      const startNode = getNodeForCFI(doc, range.start, epubOptions);
      const endNode = getNodeForCFI(doc, range.end, epubOptions);
      const markup = (theTextNode, textBits) => {
          const thingsAdded = [];
          const thingsHaveChanged = theTextNode.textContent !== textBits.join("");
          if (thingsHaveChanged) {
              return;
          }
          textBits.forEach((bit, index) => {
              var _a, _b;
              if (bit.length > 0) {
                  if (index === 1) {
                      const newChild = doc.createElement("span");
                      newChild.textContent = bit;
                      newChild.classList.add(VST_IGNORE);
                      newChild.setAttribute(IDENTIFIER_ATTRIBUTE, spanIdentifier);
                      if (renderFn)
                          renderFn(newChild, index);
                      storedBits.push(newChild);
                      if (newChild.firstChild) {
                          const text = newChild.firstChild;
                          text.markupId = spanIdentifier;
                          thingsAdded.push(text);
                          if (text) {
                              text.ignoreNode = newChild;
                              text.replaces = theTextNode;
                          }
                      }
                      shouldMarkupTextNode(theTextNode) && ((_a = theTextNode.parentNode) === null || _a === void 0 ? void 0 : _a.insertBefore(newChild, theTextNode));
                  }
                  else {
                      const newTextNode = doc.createTextNode(bit);
                      newTextNode.replaces = theTextNode;
                      thingsAdded.push(newTextNode);
                      newTextNode.markupId = spanIdentifier;
                      (_b = theTextNode.parentNode) === null || _b === void 0 ? void 0 : _b.insertBefore(newTextNode, theTextNode);
                  }
              }
          });
          if (thingsAdded.length) {
              theTextNode.replacedByTheseTextNodes = thingsAdded;
              const win = doc.defaultView;
              if (win) {
                  win.markups = win.markups || {};
                  win.markups[spanIdentifier] = win.markups[spanIdentifier] || [];
                  win.markups[spanIdentifier].push(theTextNode);
              }
              theTextNode.removedByMe = true;
              setTimeout(() => {
                  theTextNode.removedByMe = false;
              });
              theTextNode.textContent = "";
              theTextNode.hidden = true;
          }
      };
      const textNodeSets = getTextNodeSetsInRange(doc, range, epubOptions);
      const storedBits = [];
      textNodeSets.forEach((tns) => {
          var _a, _b;
          let startFoundIndex = 0;
          let endFoundIndex = tns.length - 1;
          let startCorrectionIndex = 0;
          let endCorrectionIndex = 0;
          let nodeSets = tns;
          const summary = tns.reduce((acc, n) => {
              const nodeContent = n.textContent || "";
              return {
                  fullString: acc.fullString + nodeContent,
                  nodes: [
                      ...acc.nodes,
                      {
                          node: n,
                          startIndex: acc.fullString.length,
                          endIndex: acc.fullString.length + nodeContent.length,
                      },
                  ],
              };
          }, { fullString: "", nodes: [] });
          if (tns.indexOf(startNode) !== -1 && tns.length > 0) {
              const index = findClosestIndexToOffset(range.start.charOffset, summary.fullString, 0);
              startFoundIndex = summary.nodes.findIndex((n) => n.startIndex <= index && n.endIndex > index);
              if (startFoundIndex === -1) {
                  startFoundIndex = 0;
              }
              startCorrectionIndex = index - ((_a = summary.nodes[startFoundIndex]) === null || _a === void 0 ? void 0 : _a.startIndex);
          }
          if (tns.indexOf(endNode) !== -1 && tns.length > 0) {
              const index = findClosestIndexToOffset(range.end.charOffset, summary.fullString, 0);
              endFoundIndex = summary.nodes.findIndex((n) => n.startIndex <= index && n.endIndex > index);
              if (endFoundIndex === -1) {
                  endFoundIndex = summary.nodes.length - 1;
              }
              endCorrectionIndex = index - ((_b = summary.nodes[endFoundIndex]) === null || _b === void 0 ? void 0 : _b.startIndex);
          }
          nodeSets = summary.nodes
              .slice(startFoundIndex, endFoundIndex + 1)
              .map((n) => n.node);
          nodeSets.forEach((tn) => {
              const tnText = tn.textContent || "";
              let startIndex = 0;
              let endIndex = tnText.length;
              if (tn === startNode) {
                  const charOffset = range.start.charOffset;
                  if (startCorrectionIndex !== undefined) {
                      startIndex = startCorrectionIndex;
                  }
                  else if (charOffset) {
                      startIndex = charOffset.offset;
                  }
              }
              if (tn === endNode) {
                  const charOffset = range.end.charOffset;
                  if (endCorrectionIndex !== undefined) {
                      endIndex = endCorrectionIndex;
                  }
                  else if (charOffset) {
                      endIndex = charOffset.offset;
                  }
              }
              const textBits = [
                  tnText.slice(0, startIndex) || "",
                  tnText.slice(startIndex, endIndex) || "",
                  tnText.slice(endIndex) || "",
              ];
              markup(tn, textBits);
          });
          const observerCallback = (mutationList, observer) => {
              const removedNodes = mutationList.reduce((acc, mu) => [
                  ...acc,
                  ...mu.removedNodes,
              ], []);
              const removedTextNodes = getTextNodes(removedNodes);
              removedTextNodes.forEach((rtn) => {
                  if (!rtn.removedByMe && rtn.replacedByTheseTextNodes && !rtn.replaces) {
                      rtn.replacedByTheseTextNodes.forEach((e) => {
                          recursivelyRemove(e);
                      });
                  }
              });
              mutationList.forEach((mu) => {
                  if (mu.type === "characterData") {
                      const target = mu.target;
                      if (target && !target.replaces) {
                          const textContent = target.textContent || "";
                          if (textContent.length > 0) {
                              (mu.target.replacedByTheseTextNodes || []).forEach((e) => {
                                  recursivelyRemove(e);
                              });
                          }
                      }
                  }
              });
          };
          if (doc.defaultView) {
              const observer = new doc.defaultView.MutationObserver(observerCallback);
              if (!doc.hasObserver) {
                  doc.hasObserver = true;
                  observer.observe(doc, {
                      characterData: true,
                      attributes: true,
                      childList: true,
                      subtree: true,
                  });
              }
          }
      });
      return {
          nodes: storedBits,
          identifier: spanIdentifier,
          unmark: () => unmarkDOM(doc, { identifier: spanIdentifier }),
      };
  };
  const unmarkDOM = (doc, input, options) => {
      if (input.identifier) {
          const win = doc.defaultView;
          const markups = (win === null || win === void 0 ? void 0 : win.markups) && win.markups[input.identifier];
          if (markups && win) {
              markups.forEach((replacedNode) => {
                  var _a;
                  if (replacedNode && ((_a = replacedNode.replacedByTheseTextNodes) === null || _a === void 0 ? void 0 : _a.length)) {
                      replacedNode.replacedByTheseTextNodes.forEach((addedTN) => {
                          const ignoreNode = addedTN.ignoreNode;
                          if (ignoreNode &&
                              ignoreNode.getAttribute(IDENTIFIER_ATTRIBUTE) ===
                                  input.identifier) {
                              Array.from(ignoreNode.childNodes).forEach((cn) => {
                                  var _a;
                                  (_a = ignoreNode.parentElement) === null || _a === void 0 ? void 0 : _a.insertBefore(cn, ignoreNode);
                              });
                              ignoreNode.remove();
                              addedTN.ignoreNode = undefined;
                          }
                      });
                      if (replacedNode.replacedByTheseTextNodes.every((n) => !n.hidden && !n.ignoreNode)) {
                          const firstReplacedNode = replacedNode.replacedByTheseTextNodes[0];
                          const replacedNodes = replacedNode.replacedByTheseTextNodes;
                          replacedNode.replacedByTheseTextNodes = undefined;
                          const parent = firstReplacedNode.parentElement;
                          if (parent) {
                              parent.insertBefore(replacedNode, firstReplacedNode);
                          }
                          replacedNode.textContent = replacedNodes.reduce((acc, n) => acc + n.textContent, "");
                          replacedNode.hidden = false;
                          replacedNodes.forEach((a) => a.remove());
                      }
                      else {
                          replacedNode.replacedByTheseTextNodes = replacedNode.replacedByTheseTextNodes.reduce((acc, tn) => {
                              if (tn.replacedByTheseTextNodes) {
                                  tn.replacedByTheseTextNodes.forEach((n) => {
                                      if (n.markupId && n.replaces) {
                                          const replacedIndex = win.markups[n.markupId].indexOf(n.replaces);
                                          win.markups[n.markupId][replacedIndex] = replacedNode;
                                      }
                                      n.replaces = replacedNode;
                                  });
                                  return [...acc, ...tn.replacedByTheseTextNodes];
                              }
                              else {
                                  return [...acc, tn];
                              }
                          }, []);
                      }
                  }
              });
              delete win.markups[input.identifier];
          }
      }
  };
  const insertAdjacentElementSafely = (position, node, ref, identifier) => {
      const markupID = identifier || generateUUID();
      node.classList.add(VST_SKIP);
      node.setAttribute(IDENTIFIER_ATTRIBUTE, markupID);
      return {
          node: ref.insertAdjacentElement(position, node),
          identifier: markupID,
          remove: () => node.remove(),
      };
  };
  const getNodesByIdentifier = (doc, identifier) => {
      return Array.from(doc.querySelectorAll(`[${IDENTIFIER_ATTRIBUTE}="${identifier}"]`));
  };
  const getNodeIdentifier = (node) => {
      return node.getAttribute(IDENTIFIER_ATTRIBUTE);
  };
  const nodeHasIdentifier = (node, identifier) => {
      return node.getAttribute(IDENTIFIER_ATTRIBUTE) === identifier;
  };
  const isSpatialElement = (element, _options) => {
      return (["img", "video", "audio"].indexOf(element.tagName.toLowerCase()) !== -1);
  };

  const correctBoundingRectToBeAbsolute = (boundingRect, scrollLeft = 0, scrollTop = 0) => {
      const yOffset = boundingRect.top + scrollTop;
      const xOffset = boundingRect.left + scrollLeft;
      return {
          bottom: boundingRect.bottom + scrollTop,
          height: boundingRect.height,
          right: boundingRect.right + scrollLeft,
          width: boundingRect.width,
          x: xOffset,
          y: yOffset,
          left: xOffset,
          top: yOffset,
      };
  };
  const getAbsoluteBoundingRectOfRange = (range, scrollLeft = 0, scrollTop = 0) => {
      var _a;
      let rect = range.getClientRects()[0];
      let rectParent = null;
      if (range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE) {
          rectParent = range.commonAncestorContainer;
      }
      else if (range.commonAncestorContainer.nodeType === Node.TEXT_NODE) {
          rectParent = range.commonAncestorContainer.parentElement;
      }
      while (!rect && rectParent) {
          rect = (_a = rectParent.getClientRects) === null || _a === void 0 ? void 0 : _a.call(rectParent)[0];
          rectParent = rectParent.parentElement;
      }
      if (!rect) {
          rect = range.getBoundingClientRect();
      }
      return correctBoundingRectToBeAbsolute(rect, scrollLeft, scrollTop);
  };
  const getAbsoluteBoundingRect = (element, scrollLeft = 0, scrollTop = 0) => {
      var _a;
      const doc = element.ownerDocument;
      if (isTextNode(element)) {
          const range = doc === null || doc === void 0 ? void 0 : doc.createRange();
          if (range) {
              range.setStart(element, 0);
              range.setEnd(element, (((_a = element.textContent) === null || _a === void 0 ? void 0 : _a.length) || 0) - 1);
              const boundingRect = getAbsoluteBoundingRectOfRange(range, scrollLeft, scrollTop);
              range.detach();
              return boundingRect;
          }
          return {
              x: 0,
              y: 0,
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              height: 0,
              width: 0,
          };
      }
      else {
          return correctBoundingRectToBeAbsolute(element.getBoundingClientRect(), scrollLeft, scrollTop);
      }
  };
  const isBeforeXY = (rect, x, y) => {
      return (y > rect.bottom || (y >= rect.top && y <= rect.bottom && x > rect.left));
  };
  const isDirectHit = (rect, x, y) => {
      return y >= rect.top && y <= rect.bottom && x >= rect.left && x <= rect.right;
  };
  const getChildForPoint = (parent, x, y, scrollOffsets, options, prev) => {
      var _a;
      const handleReturn = (response) => {
          var _a;
          if (!isTextNode(response.element) &&
              response.element.classList.contains(VST_IGNORE)) {
              return getChildForPoint(response.element, x, y, scrollOffsets, options);
          }
          else if (response.guess && isTextNode(response.element)) {
              const textNode = response.element;
              const text = textNode.textContent;
              if (text) {
                  let closestOffset = 0;
                  for (let c = 0; c < text.length - 1; c++) {
                      const range = (_a = textNode.ownerDocument) === null || _a === void 0 ? void 0 : _a.createRange();
                      if (range) {
                          range.setStart(textNode, c);
                          range.setEnd(textNode, c + 1);
                          const boundingRect = getAbsoluteBoundingRectOfRange(range, scrollOffsets.left, scrollOffsets.top);
                          range.detach();
                          if (isBeforeXY(boundingRect, x, y)) {
                              closestOffset = c;
                          }
                          else {
                              break;
                          }
                      }
                  }
                  const closest = { offset: closestOffset };
                  const textNodes = getNormalizedTextNodes(textNode, options);
                  const index = textNodes === null || textNodes === void 0 ? void 0 : textNodes.indexOf(textNode);
                  const offsetOffset = textNodes === null || textNodes === void 0 ? void 0 : textNodes.slice(0, index).reduce((acc, tn) => acc + (tn.textContent || "").length, 0);
                  const offset = offsetOffset + closest.offset;
                  return {
                      element: textNode,
                      guess: true,
                      characterOffset: {
                          offset,
                          assert: {
                              precedingText: text.slice(closest.offset - 3, closest.offset),
                              trailingText: text.slice(closest.offset, closest.offset + 3),
                          },
                      },
                  };
              }
              else {
                  return response;
              }
          }
          else if (!response.guess &&
              !isTextNode(response.element) &&
              isSpatialElement(response.element)) {
              const boundingRect = getAbsoluteBoundingRect(response.element, scrollOffsets.left, scrollOffsets.top);
              return Object.assign(Object.assign({}, response), { spatialOffset: {
                      x: Math.max(0, Math.min(100, Math.round(((x - boundingRect.left) / boundingRect.width) * 100.0))),
                      y: Math.max(0, Math.min(100, Math.round(((y - boundingRect.top) / boundingRect.height) * 100.0))),
                  } });
          }
          else {
              return response;
          }
      };
      let previousElement = prev || parent;
      const children = getChildren(parent, options);
      if (parent.childNodes.length === 0) {
          return handleReturn({ element: parent, guess: true });
      }
      let onlyAllowDirectHits = false;
      for (let i = 0; i < children.length; i++) {
          const child = children[i];
          if (child.nodeType === 3) {
              const text = child.textContent;
              if (text) {
                  for (let c = 0; c < text.length - 1; c++) {
                      const range = (_a = child.ownerDocument) === null || _a === void 0 ? void 0 : _a.createRange();
                      if (range) {
                          range.setStart(child, c);
                          range.setEnd(child, c + 1);
                          const boundingRect = getAbsoluteBoundingRectOfRange(range, scrollOffsets.left, scrollOffsets.top);
                          range.detach();
                          if (isDirectHit(boundingRect, x, y)) {
                              const textNodes = getNormalizedTextNodes(child, options);
                              const index = textNodes === null || textNodes === void 0 ? void 0 : textNodes.indexOf(child);
                              const totalText = textNodes.reduce((acc, tn) => acc + tn.textContent, "");
                              const offsetOffset = textNodes === null || textNodes === void 0 ? void 0 : textNodes.slice(0, index).reduce((acc, tn) => acc + (tn.textContent || "").length, 0);
                              const offset = offsetOffset + c;
                              return {
                                  element: child,
                                  guess: false,
                                  characterOffset: {
                                      offset,
                                      assert: {
                                          precedingText: totalText.slice(offset - 3, offset),
                                          trailingText: totalText.slice(offset, offset + 3),
                                      },
                                  },
                              };
                          }
                          else if (isBeforeXY(boundingRect, x, y)) {
                              previousElement = child;
                              onlyAllowDirectHits = true;
                          }
                      }
                  }
              }
              if (!onlyAllowDirectHits) {
                  previousElement = child;
              }
              continue;
          }
          else {
              const boundingRect = child.getBoundingClientRect();
              if (child.classList.contains(VST_SKIP) ||
                  (boundingRect &&
                      (boundingRect.width === 0 || boundingRect.height === 0))) {
                  continue;
              }
              const rect = getAbsoluteBoundingRect(child, scrollOffsets.left, scrollOffsets.top);
              if (isDirectHit(rect, x, y)) {
                  const newChild = getChildForPoint(child, x, y, scrollOffsets, options, child);
                  if (!newChild.guess) {
                      return newChild;
                  }
                  else if (newChild.element === child) {
                      return handleReturn({ element: child, guess: false });
                  }
                  else {
                      if (onlyAllowDirectHits) {
                          if (isBeforeXY(rect, x, y)) {
                              const prevRect = getAbsoluteBoundingRect(previousElement, scrollOffsets.left, scrollOffsets.top);
                              if (prevRect.bottom <= rect.bottom) {
                                  previousElement = newChild.element;
                              }
                          }
                      }
                      else {
                          onlyAllowDirectHits = true;
                          previousElement = newChild.element;
                      }
                      continue;
                  }
              }
              else if (onlyAllowDirectHits) {
                  if (isBeforeXY(rect, x, y)) {
                      const prevRect = getAbsoluteBoundingRect(previousElement, scrollOffsets.left, scrollOffsets.top);
                      if (prevRect.bottom <= rect.bottom) {
                          const newChild = getChildForPoint(child, x, y, scrollOffsets, options);
                          if (!newChild.guess) {
                              return newChild;
                          }
                          previousElement = newChild.element;
                      }
                  }
                  continue;
              }
              else if (y >= rect.bottom) {
                  previousElement = child;
                  continue;
              }
              else if (y <= rect.top && !isInline(child)) {
                  if (previousElement === parent || previousElement === child) {
                      return handleReturn({ element: previousElement, guess: true });
                  }
                  else {
                      return getChildForPoint(previousElement, x, y, scrollOffsets, options);
                  }
              }
          }
          previousElement = child;
      }
      return handleReturn({ element: previousElement, guess: true });
  };
  const isInline = (child) => {
      var _a;
      const win = (_a = child.ownerDocument) === null || _a === void 0 ? void 0 : _a.defaultView;
      if (win) {
          return win.getComputedStyle(child, "").display === "inline";
      }
      else {
          return false;
      }
  };
  const getScrollOffsets = (el) => {
      var _a;
      const scrollEl = (_a = el.ownerDocument) === null || _a === void 0 ? void 0 : _a.scrollingElement;
      if (scrollEl) {
          return { left: scrollEl.scrollLeft, top: scrollEl.scrollTop };
      }
      return { left: 0, top: 0 };
  };
  const getCFIChildForPoint = (parent, x, y, options) => {
      const scrollOffsets = getScrollOffsets(parent);
      const epubOptions = options || {};
      const r = getChildForPoint(parent, x + scrollOffsets.left, y + scrollOffsets.top, scrollOffsets, epubOptions);
      const doc = r.element.ownerDocument;
      if (doc) {
          return getNodeForCFI(doc, getCFIForPoint(parent, x + scrollOffsets.left, y + scrollOffsets.top, epubOptions), epubOptions);
      }
      return null;
  };
  const getCFIForPoint = (parent, x, y, options) => {
      const scrollOffsets = getScrollOffsets(parent);
      const child = getChildForPoint(parent, x + scrollOffsets.left, y + scrollOffsets.top, scrollOffsets, options || {});
      const cfi = getCFIForNode(child.element, options || {});
      cfi.charOffset = child.characterOffset;
      cfi.spatialOffset = child.spatialOffset;
      return cfi;
  };
  const locateCFI = (doc, cfi, options) => {
      const scrollOffsets = getScrollOffsets(doc.body);
      const opts = options || {};
      const node = getNodeForCFI(doc, cfi, opts);
      if (node) {
          const { x, y, width, height } = getAbsoluteBoundingRect(node, scrollOffsets.left, scrollOffsets.top);
          let newX = x;
          let newY = y;
          if (cfi.spatialOffset && isSpatialElement(node)) {
              newX = x + width * (cfi.spatialOffset.x / 100.0);
              newY = y + height * (cfi.spatialOffset.y / 100.0);
          }
          if (cfi.charOffset && isTextNode(node)) {
              const textNodes = getNormalizedTextNodes(node, opts);
              const index = textNodes.indexOf(node);
              const fullTextUpToNode = textNodes
                  .slice(0, index)
                  .reduce((acc, tn) => acc + tn.textContent, "");
              const range = doc.createRange();
              const correctedOffset = cfi.charOffset.offset - fullTextUpToNode.length;
              range.setStart(node, correctedOffset);
              range.setEnd(node, correctedOffset + 1);
              const { x: charX, y: charY } = getAbsoluteBoundingRectOfRange(range, scrollOffsets.left, scrollOffsets.top);
              range.detach();
              return { x: charX, y: charY, node };
          }
          return { x: newX, y: newY, node };
      }
      else {
          return { x: 0, y: 0 };
      }
  };

  exports.CFI = CFI;
  exports.CFIRange = CFIRange;
  exports.getCFIChildForPoint = getCFIChildForPoint;
  exports.getCFIForNode = getCFIForNode;
  exports.getCFIForPoint = getCFIForPoint;
  exports.getCFIForRange = getCFIForRange;
  exports.getChildForPoint = getChildForPoint;
  exports.getCorrectedCFI = getCorrectedCFI;
  exports.getNodeForCFI = getNodeForCFI;
  exports.getNodeIdentifier = getNodeIdentifier;
  exports.getNodesByIdentifier = getNodesByIdentifier;
  exports.insertAdjacentElementSafely = insertAdjacentElementSafely;
  exports.locateCFI = locateCFI;
  exports.markupDOM = markupDOM;
  exports.markupHighlight = markupHighlight;
  exports.markupNodes = markupNodes;
  exports.nodeHasIdentifier = nodeHasIdentifier;
  exports.parseCfi = parseCfi;
  exports.parseCfiRange = parseCfiRange;
  exports.unmarkDOM = unmarkDOM;

  Object.defineProperty(exports, '__esModule', { value: true });

})));
